import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { MdArrowBack, MdArrowForward, MdAccessTime, MdCalendarToday } from "react-icons/md";
import { Exercise } from "../../../../../models/Exercise";
import { Assort } from "../../../../../models/Assort";
import { TicketAccess } from "../../../../../models/TicketAccess";
import { BasketItem } from "../../../../../models/BasketItem";
import { BsBasket3, BsPeople } from "react-icons/bs";
import EAAlert from "../../../../shared/EAAlert";
import EASkeletonLoader from "../../../../shared/EASkeletonLoader";
import EAButton from "../../../../shared/Buttons/EAButton";
import { useTranslation } from "react-i18next";
import TicketCard from "./TicketCard";
import "./CourtTicketSelection.scss";
import useCourtTicketSelection from "./useCourtTicketSelection";
import { SaleStep } from "../../../../../store/stepSaleConfig/stepSaleConfigReducer";
import { State } from "../../../../../store";
import EAModal from "../../../../shared/Modal/EAModal";
import { getAvailablePlacesCount, pluralMiejsc } from "../../../../shared/TimeSlot/utils";
import useFacility from "../../../../../../utils/hooks/useFacility";
import { isKarnetTestFacility } from "./karnetConcept";
import useKarnetPurchase from "./useKarnetPurchase";
import KarnetSection from "./KarnetSection";
import KarnetTermsConfirmation from "./KarnetTermsConfirmation";

interface IProps {
  exerciseId: number;
  exerciseDetails: Exercise | null;
  tickets: Assort[];
  ticketAccess: TicketAccess;
  ticketLimit: number;
  onTicketsAdded: () => void;
  onBack: () => void;
  saleStep?: SaleStep;
}

const CourtTicketSelection: React.FC<IProps> = ({
  exerciseId,
  exerciseDetails,
  tickets: passedTickets,
  ticketAccess,
  ticketLimit,
  onTicketsAdded,
  onBack,
  saleStep,
}) => {
  const { t } = useTranslation();

  const showTimeOfEntance = useSelector((state: State) => state.config.configuration.showTimeOfEntance);
  const showTicketsPoolForStepByStepSale = useSelector(
    (state: State) => state.config.configuration.showTicketsPoolForStepByStepSale,
  );

  const {
    getQnt,
    getEffectiveMax,
    handleQuantityChange,
    addItemToBasket,
    removeItemFromBasket,
    sumBasket,
    saveBasket,
    alert,
    setAlert,
    openTooltip,
    setOpenTooltip,
    preBasket,
    ticketsInCurrentBasket,
    isSavingBasket,
  } = useCourtTicketSelection(exerciseId, ticketAccess, ticketLimit, onTicketsAdded);

  const facilityIds = useFacility();
  const hasKarnetEligibleInBasket = isKarnetTestFacility(facilityIds) && preBasket.some((item) => item.qnt > 0);

  const karnet = useKarnetPurchase({
    exerciseDetails,
    saleStep,
    anchorTicketAccess: ticketAccess,
    preBasket,
    onPurchased: onTicketsAdded,
  });

  // Scroll shadow handling for tickets list
  const ticketsListRef = useRef<HTMLDivElement | null>(null);
  const [hasTopShadow, setHasTopShadow] = useState(false);
  const [hasBottomShadow, setHasBottomShadow] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);

  const updateShadows = () => {
    const el = ticketsListRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setHasTopShadow(scrollTop > 0);
    setHasBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
  };

  useEffect(() => {
    updateShadows();
    const el = ticketsListRef.current;
    if (el) {
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      setShowScrollHint(hasOverflow);
    }
  }, [passedTickets.length]);

  if (!exerciseDetails || passedTickets.length === 0) {
    return <EASkeletonLoader height="200px" />;
  }

  const startMs = exerciseDetails.startTime;
  const closeMs = exerciseDetails.closeTime;
  const availablePlaces = getAvailablePlacesCount(exerciseDetails.capacity, exerciseDetails.reserved);
  const weekdayLabel = format(startMs, "EEEE", { locale: pl });
  const dateLabel = format(startMs, "dd.MM.yyyy", { locale: pl });
  const timeLabel = showTimeOfEntance
    ? `${format(startMs, "HH:mm")} – ${format(closeMs, "HH:mm")}`
    : format(startMs, "HH:mm");
  const showPlacesInSummary = !showTimeOfEntance && showTicketsPoolForStepByStepSale;

  const handleOnDecrement = (ticket: Assort, ticketAccess: TicketAccess) => {
    setShowLimitAlert(false);
    removeItemFromBasket(ticket, ticketAccess);
  };

  return (
    <div className="court-ticket-selection">
      <div className="court-ticket-selection__summary">
        <h4>{exerciseDetails.name}</h4>
        <div className="court-ticket-selection__meta">
          <span className="court-ticket-selection__meta-item">
            <MdCalendarToday className="court-ticket-selection__meta-icon" aria-hidden />
            <span className="court-ticket-selection__meta-text">
              {weekdayLabel}, {dateLabel}
            </span>
          </span>
          <span className="court-ticket-selection__meta-item">
            <MdAccessTime className="court-ticket-selection__meta-icon" aria-hidden />
            <span className="court-ticket-selection__meta-text">{timeLabel}</span>
          </span>
          {showPlacesInSummary && (
            <span className="court-ticket-selection__meta-item">
              <BsPeople className="court-ticket-selection__meta-icon" aria-hidden />
              <span className="court-ticket-selection__meta-text">
                {availablePlaces} {pluralMiejsc(availablePlaces)}
              </span>
            </span>
          )}
        </div>
      </div>

      {karnet.step === "confirm" ? (
        <KarnetTermsConfirmation
          terms={karnet.selectedTerms}
          availableCount={karnet.availableTerms.length}
          unavailableCount={karnet.unavailableCount}
          totalPrice={sumBasket() * karnet.availableTerms.length}
          isAdding={karnet.isAdding}
          addError={karnet.addError}
          onConfirm={karnet.confirmPurchase}
          onBack={karnet.backToSelect}
        />
      ) : (
        <>
          {alert.length > 2 && alert !== t("dodano_bilet_do_koszyka") && <EAAlert type="error" message={alert} />}

          <div
            ref={ticketsListRef}
            className={
              `court-ticket-selection__tickets` +
              (hasTopShadow ? " has-top-shadow" : "") +
              (hasBottomShadow ? " has-bottom-shadow" : "")
            }
            onScroll={(e) => {
              updateShadows();
              const el = e.currentTarget as HTMLDivElement;
              if (el.scrollTop > 0 && showScrollHint) {
                setShowScrollHint(false);
              }
            }}
          >
            {passedTickets.map((item, index) => {
              const currentQnt = getQnt(item);
              const effectiveMax = getEffectiveMax(item);

              return (
                <TicketCard
                  key={index}
                  ticket={item}
                  currentQuantity={currentQnt}
                  effectiveMax={effectiveMax}
                  onQuantityChange={handleQuantityChange}
                  onIncrement={addItemToBasket}
                  onDecrement={(ticket, ticketAccess) => handleOnDecrement(ticket, ticketAccess)}
                  ticketAccess={ticketAccess}
                  openTooltip={openTooltip}
                  setOpenTooltip={setOpenTooltip}
                  onIncrementDisabledClick={() => setShowLimitAlert(true)}
                />
              );
            })}
            {showScrollHint && (
              <div className="court-ticket-selection__scroll-hint">
                <span>Przewiń, aby zobaczyć więcej biletów</span>
              </div>
            )}
          </div>

          {ticketLimit === ticketsInCurrentBasket() && showLimitAlert && (
            <EAAlert type="error" message={t("nie_mozesz_dodac_wiekszej_ilosci_biletow_do_koszyka")} />
          )}

          {saleStep?.showOtherTicketsPool && ticketLimit > 0 && ticketLimit !== ticketsInCurrentBasket() && (
            <EAAlert
              type="warning"
              message={`${t("na_to_wejscie_obowiazuje_limit_biletow_pozostalo")} ${
                ticketLimit - ticketsInCurrentBasket()
              }`}
            />
          )}

          {hasKarnetEligibleInBasket && (
            <KarnetSection
              enabled={karnet.enabled}
              onToggle={karnet.toggleEnabled}
              clampedRepeats={karnet.clampedRepeats}
              onRepeatsChange={karnet.setRepeats}
              horizonCount={karnet.schedule.horizonCount}
              lastAvailableDateLabel={karnet.schedule.lastAvailableDateLabel}
              loading={karnet.schedule.loading}
              perTermPrice={sumBasket()}
            />
          )}

          <div className="court-ticket-selection__footer">
            <div className="court-ticket-selection__total">
              {t("suma")}:{" "}
              <strong>
                {(karnet.enabled ? sumBasket() * karnet.clampedRepeats : sumBasket()).toFixed(2)} {t("zl")}
              </strong>
            </div>
            <div className="court-ticket-selection__actions">
              <EAButton
                title={karnet.enabled ? "Dalej — sprawdź terminy" : t("dodaj_do_koszyka")}
                onClick={karnet.enabled ? karnet.goToConfirmation : saveBasket}
                endIcon={karnet.enabled ? <MdArrowForward /> : <BsBasket3 />}
                variant="filled"
                color="primary"
                fullWidth
                loading={karnet.enabled ? false : isSavingBasket}
                disabled={
                  karnet.enabled
                    ? karnet.schedule.loading || karnet.clampedRepeats < 1
                    : preBasket.reduce((acc: number, item: BasketItem) => acc + item.qnt, 0) === 0
                }
              />
              <EAButton
                title="Powrót"
                onClick={() => {
                  setAlert("");
                  onBack();
                }}
                startIcon={<MdArrowBack />}
                variant="text"
                fullWidth
              />
            </div>
          </div>
        </>
      )}

      {openTooltip && (
        <EAModal
          isOpen={!!openTooltip}
          onClose={() => setOpenTooltip(null)}
          title={passedTickets.find((t) => t.name === openTooltip)?.displayWebName || ""}
          cancelButtonLabel={t("zamknij")}
          hideAcceptButton
        >
          <article
            dangerouslySetInnerHTML={{
              __html: passedTickets.find((t) => t.name === openTooltip)?.description || "",
            }}
          />
        </EAModal>
      )}
    </div>
  );
};

export default CourtTicketSelection;
